import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  canAutoOpenFromCurrentPath,
  DEFAULT_FILE_PREFIX,
} from './routePath';
import {
  createChecklistItemTemplateAutoOpenCheck,
  createComplianceCheckAutoOpenCheck,
  createDocumentAutoOpenCheck,
  createGeneralNormAutoOpenCheck,
  createProtocolSectionAutoOpenCheck,
  createSectionTemplateAutoOpenCheck,
  createTemplateGeneralNormAutoOpenCheck,
  DocumentAutoOpenCheck,
} from './routeAutoOpenChecks';

export type InspectionDocumentRouteSync = {
  internalEntityBaseUrl: (() => string) | undefined;
  autoOpenCheck: DocumentAutoOpenCheck | undefined;
};

function useDocumentRouteSync(
  autoOpenCheck: DocumentAutoOpenCheck | undefined,
  getBaseUrl: (() => string) | undefined
): InspectionDocumentRouteSync {
  return useMemo(
    () => ({
      internalEntityBaseUrl: getBaseUrl,
      autoOpenCheck,
    }),
    [autoOpenCheck, getBaseUrl]
  );
}

export function useDocumentAutoOpenCheck(
  pathMarkers: readonly string[]
): DocumentAutoOpenCheck {
  const { pathname } = useLocation();
  const markersKey = pathMarkers.join('\0');

  return useMemo(
    () => createDocumentAutoOpenCheck(pathname, pathMarkers),
    // pathMarkers identity changes each render; markersKey captures content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, markersKey]
  );
}

export function useComplianceCheckAutoOpenCheck(
  checklistItemId: string,
  protocolItemId?: string
): DocumentAutoOpenCheck {
  const { pathname } = useLocation();
  return useMemo(
    () =>
      createComplianceCheckAutoOpenCheck(
        pathname,
        checklistItemId,
        protocolItemId
      ),
    [pathname, checklistItemId, protocolItemId]
  );
}

export function useTemplateGeneralNormDocumentRouteSync(
  templateId: string,
  getBaseUrl: () => string
): InspectionDocumentRouteSync {
  const { pathname } = useLocation();
  const autoOpenCheck = useMemo(
    () => createTemplateGeneralNormAutoOpenCheck(pathname, templateId),
    [pathname, templateId]
  );

  return useDocumentRouteSync(
    templateId ? autoOpenCheck : undefined,
    templateId ? getBaseUrl : undefined
  );
}

export function useChecklistItemTemplateAutoOpenCheck(
  inspectionId: string | undefined,
  templateId: string,
  checklistItemId: string
): DocumentAutoOpenCheck {
  const { pathname } = useLocation();
  return useMemo(() => {
    if (!inspectionId) {
      return createDocumentAutoOpenCheck(pathname, [
        `/inspectionTemplate/${templateId}/checklistItem/${checklistItemId}`,
        `/inspectionTemplate/${templateId}/checklist/${checklistItemId}`,
      ]);
    }
    return createChecklistItemTemplateAutoOpenCheck(
      pathname,
      inspectionId,
      templateId,
      checklistItemId
    );
  }, [checklistItemId, inspectionId, pathname, templateId]);
}

export function useSectionTemplateDocumentRouteSync(
  inspectionId: string | undefined,
  getBaseUrl: () => string,
  templateId: string,
  sectionId: string
): InspectionDocumentRouteSync {
  const { pathname } = useLocation();
  const autoOpenCheck = useMemo(
    () =>
      createSectionTemplateAutoOpenCheck(
        pathname,
        inspectionId,
        templateId,
        sectionId
      ),
    [inspectionId, pathname, sectionId, templateId]
  );

  return useDocumentRouteSync(
    templateId ? autoOpenCheck : undefined,
    templateId ? getBaseUrl : undefined
  );
}

export function useProtocolSectionDocumentRouteSync(
  inspectionId: string | undefined,
  getBaseUrl: () => string,
  protocolSectionId: string | undefined
): InspectionDocumentRouteSync {
  const { pathname } = useLocation();
  const autoOpenCheck = useMemo(() => {
    if (!inspectionId || !protocolSectionId) {
      return undefined;
    }
    return createProtocolSectionAutoOpenCheck(
      pathname,
      inspectionId,
      protocolSectionId
    );
  }, [inspectionId, pathname, protocolSectionId]);

  return useDocumentRouteSync(
    autoOpenCheck,
    inspectionId && protocolSectionId ? getBaseUrl : undefined
  );
}

export function useGeneralNormDocumentRouteSync(
  inspectionId: string | undefined,
  getBaseUrl: () => string,
  templateId: string
): InspectionDocumentRouteSync {
  const { pathname } = useLocation();
  const autoOpenCheck = useMemo(
    () =>
      createGeneralNormAutoOpenCheck(pathname, inspectionId ?? '', templateId),
    [inspectionId, pathname, templateId]
  );

  return useDocumentRouteSync(
    inspectionId ? autoOpenCheck : undefined,
    inspectionId ? getBaseUrl : undefined
  );
}

export function useDocumentDeepLinkActive(
  autoOpenCheck: DocumentAutoOpenCheck | undefined,
  internalEntityBaseUrl: (() => string) | undefined,
  filePrefix = DEFAULT_FILE_PREFIX
): boolean {
  const { pathname } = useLocation();
  const matchedId = useParams<Record<string, string | undefined>>().documentId;

  return useMemo(() => {
    if (!internalEntityBaseUrl) {
      return false;
    }

    return canAutoOpenFromCurrentPath(pathname, internalEntityBaseUrl(), [], {
      filePrefix,
      matchedId,
      autoOpenCheck,
    });
  }, [autoOpenCheck, filePrefix, internalEntityBaseUrl, matchedId, pathname]);
}
